import { useState, useRef, useEffect } from 'react';
import { Checkbox, styled } from '@mui/material';
import { Form } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import * as E from './elements';

// Styled components
const WaiverContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const WaiverTextContainer = styled('div')`
  max-height: 300px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 12px;
`;

const PDFLinkContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #f5f5f5;
  margin-bottom: 12px;
`;

const PDFIcon = styled('i')`
  font-size: 24px;
  color: #dc3545;
`;

const PDFLink = styled('a')`
  color: #9d25dc;
  text-decoration: none;
  font-weight: 500;
  flex: 1;

  &:hover {
    text-decoration: underline;
  }
`;

const SignatureContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SignatureCanvasWrapper = styled('div')`
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  width: 100%;
  max-width: 500px;

  &.error {
    border-color: #dc3545;
  }

  canvas {
    width: 100% !important;
    height: 150px !important;
    cursor: crosshair;
    touch-action: none;
  }
`;

const SignatureControls = styled('div')`
  display: flex;
  gap: 8px;
`;

const ClearButton = styled('button')`
  padding: 6px 12px;
  background-color: #f0f0f0;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const SignatureLabel = styled('label')`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const ConfirmationSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FullNameInput = styled(Form.Control)`
  max-width: 400px;
`;

/**
 * Signature Pad Component
 * Handles drawing and clearing signatures using react-signature-canvas
 */
const SignaturePad = ({ onSignatureChange, error, required }) => {
  const sigCanvas = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const handleEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsEmpty(false);
      onSignatureChange(sigCanvas.current.toDataURL());
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
      onSignatureChange('');
    }
  };

  return (
    <SignatureContainer>
      <SignatureLabel>{required ? 'Signature *' : 'Signature'}</SignatureLabel>
      <SignatureCanvasWrapper className={error ? 'error' : ''}>
        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            className: 'signature-canvas',
          }}
          onEnd={handleEnd}
          backgroundColor="white"
          penColor="black"
          minWidth={1}
          maxWidth={2.5}
          velocityFilterWeight={0.7}
        />
      </SignatureCanvasWrapper>
      <SignatureControls>
        <ClearButton type="button" onClick={clearSignature}>
          Clear Signature
        </ClearButton>
        {isEmpty && required && <E.ErrorMessage>Signature is required</E.ErrorMessage>}
      </SignatureControls>
    </SignatureContainer>
  );
};

/**
 * WaiverTermsQuestion Component
 * Handles rendering of waiver/terms questions with multiple content sources
 * and confirmation methods
 */
export const WaiverTermsQuestion = ({ question, setCustomField, errors }) => {
  const questionText = question.question;
  const isError = !!errors[`custom_${questionText}`];

  // Extract waiver configuration with backwards compatibility
  const waiverSource = question.waiver_source || 'link';
  const waiverContent = question.waiver_content || question.options; // Fallback to old options field
  const waiverFileName = question.waiver_file_name || 'waiver.pdf';
  const confirmationMethod = question.confirmation_method || 'checkbox';
  const termsLabel = question.terms_label || 'Terms';
  const linkLabel = question.link_label || 'View Terms & Conditions';

  // State for confirmation inputs
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [signature, setSignature] = useState('');
  const [fullName, setFullName] = useState('');

  // Determine if confirmation methods include checkbox, signature, or fullname
  const needsCheckbox = ['checkbox', 'checkbox_signature', 'checkbox_fullname'].includes(confirmationMethod);
  const needsSignature = ['signature', 'checkbox_signature'].includes(confirmationMethod);
  const needsFullName = ['fullname', 'checkbox_fullname'].includes(confirmationMethod);

  // Handle changes and update parent component
  const handleConfirmationChange = () => {
    let isComplete = true;
    const value = {
      // Store the waiver/document details that the user is agreeing to
      waiver: {
        source: waiverSource, // 'text', 'link', or 'upload'
        content: waiverContent, // The actual text or URL
        fileName: waiverSource === 'upload' ? waiverFileName : undefined,
        termsLabel,
        linkLabel,
        timestamp: new Date().toISOString(),
      },
      // Store the user's confirmation/agreement
      confirmation: {},
    };

    if (needsCheckbox) {
      value.confirmation.checkbox = checkboxChecked;
      isComplete = isComplete && checkboxChecked;
    }

    if (needsSignature) {
      value.confirmation.signature = signature;
      isComplete = isComplete && signature !== '';
    }

    if (needsFullName) {
      value.confirmation.fullName = fullName;
      isComplete = isComplete && fullName.trim() !== '';
    }

    // Send confirmation data to parent
    setCustomField('terms', question)(questionText, isComplete ? JSON.stringify(value) : '');
  };

  useEffect(() => {
    handleConfirmationChange();
  }, [checkboxChecked, signature, fullName]);

  return (
    <WaiverContainer>
      {/* Waiver Content Display */}
      {waiverSource === 'text' && <WaiverTextContainer>{waiverContent}</WaiverTextContainer>}

      {(waiverSource === 'link' || waiverSource === 'upload') && waiverContent && (
        <PDFLinkContainer>
          <PDFIcon className="pi pi-file-pdf" />
          <PDFLink href={waiverContent} target="_blank" rel="noreferrer" download={waiverSource === 'upload' ? waiverFileName : undefined}>
            {linkLabel}
          </PDFLink>
          <i className="pi pi-external-link" style={{ color: '#9d25dc' }} />
        </PDFLinkContainer>
      )}

      {/* Confirmation Section */}
      <ConfirmationSection>
        {needsCheckbox && (
          <E.TermsForm type="checkbox" isInvalid={isError && !checkboxChecked}>
            <Checkbox
              icon={<img src="https://cdn.ticketspot.io/widget/layout-icons/custom-checkout-icon.svg" alt="o" />}
              checkedIcon={<img src="https://cdn.ticketspot.io/widget/layout-icons/custom-checkout-checked.svg" alt="x" />}
              checked={checkboxChecked}
              onChange={(e) => setCheckboxChecked(e.target.checked)}
              id={`checkbox-${questionText}`}
              name={`checkbox-option-${questionText}`}
            />
            <E.Label as="span">{termsLabel}</E.Label>
          </E.TermsForm>
        )}

        {needsSignature && <SignaturePad onSignatureChange={setSignature} error={isError && !signature} required={question.required} />}

        {needsFullName && (
          <div>
            <SignatureLabel>{question.required ? 'Full Name *' : 'Full Name'}</SignatureLabel>
            <FullNameInput
              type="text"
              placeholder="Enter your full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              isInvalid={isError && !fullName.trim()}
              required={question.required}
            />
          </div>
        )}
      </ConfirmationSection>

      {/* Error Message */}
      {isError && <E.ErrorMessage>{errors[`custom_${questionText}`]}</E.ErrorMessage>}
    </WaiverContainer>
  );
};
